import { FiSearch } from 'react-icons/fi';
import BackButton from '../../../../components/ui/BackButton';
import MobileMenuButton from '../../../../components/ui/MobileMenuButton';
import Select from '../../../../components/ui/Select';

/**
 * Persistent two-row dark header for mobile testing sessions.
 *
 * Row 1 — back arrow · site name + subtitle · menu kebab (dot badge when remaining > 0)
 * Row 2 — asset-type pill · scoped search field
 *
 * Props:
 *   siteName             — site display name (truncated with ellipsis)
 *   subtitle             — list-mode label e.g. "DISTRIBUTION BOARD TESTING"
 *   remaining            — untested count; >0 shows a dot badge on the kebab
 *   onBack               — back-arrow handler
 *   onMenuOpen           — kebab handler (opens session summary sheet)
 *   sessionAssetType     — active asset type value
 *   assetOptionsForElement — [{ value, label }]
 *   onAssetTypeChange    — called with { target: { value } }
 *   searchValue          — controlled search input value
 *   onSearchChange       — search input onChange handler
 *   searchPlaceholder    — scope-aware placeholder text
 *
 * Group-mode scope line (replaces subtitle when scopeGroup is set):
 *   scopeGroup           — e.g. "DB.1" — shows a gold chip
 *   scopeLocation        — e.g. "B00A / R1040"
 * The count now lives in the drill-down body (spec §1/§3), not the header.
 */
export default function MobileSessionHeader({
  siteName,
  subtitle,
  remaining = 0,
  scopeGroup = null,
  scopeLocation = '',
  onBack,
  onMenuOpen,
  sessionAssetType,
  assetOptionsForElement,
  onAssetTypeChange,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search equipment code',
}) {
  return (
    <header className="mobile-session-header">
      <div className="mobile-session-header__row1">
        <BackButton
          onClick={onBack}
          ariaLabel="Go back"
          className="mobile-session-header__back"
        />
        <div className="mobile-session-header__title-block">
          <span className="mobile-session-header__site">{siteName}</span>
          {scopeGroup ? (
            <span className="mobile-session-header__scope-row">
              <span className="mobile-session-header__scope-chip">{scopeGroup}</span>
              {scopeLocation && (
                <span className="mobile-session-header__scope-location">{scopeLocation}</span>
              )}
              <span className="mobile-session-header__scope-label">CIRCUITS IN THIS BOARD</span>
            </span>
          ) : (
            <span className="mobile-session-header__subtitle">{subtitle}</span>
          )}
        </div>
        <div className="mobile-session-header__menu-wrap">
          <MobileMenuButton
            onClick={onMenuOpen}
            ariaLabel="Session options"
            badge={remaining > 0}
          />
        </div>
      </div>

      <div className="mobile-session-header__row2">
        <Select
          variant="pill"
          id="mobile-session-asset-type"
          aria-label="Asset type"
          value={sessionAssetType}
          onChange={onAssetTypeChange}
          className="mobile-session-header__asset-type"
        >
          {assetOptionsForElement.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Select>
        <div className="mobile-session-header__search-wrap">
          <FiSearch size={14} aria-hidden className="mobile-session-header__search-icon" />
          <input
            type="search"
            className="mobile-session-header__search"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={onSearchChange}
            aria-label={searchPlaceholder}
          />
        </div>
      </div>
    </header>
  );
}
